import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Loader2, AlertCircle } from 'lucide-react';

const BarChartComponent = ({
  data = [],
  bars = [{ key: 'value', name: 'Value', color: '#0052FF' }],
  xAxisKey = 'name',
  layout = 'horizontal', // 'horizontal' | 'vertical'
  stacked = false,
  height = 300,
  loading = false,
  error = null,
  title = '',
  formatter = (v) => v,
  onBarClick = null,
}) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center bg-white border border-slate-200 rounded-xl p-6" style={{ height }}>
        <Loader2 className="w-8 h-8 animate-spin text-[#0052FF] mb-2" />
        <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider">Loading Chart...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center bg-white border border-slate-200 rounded-xl p-6 text-center" style={{ height }}>
        <AlertCircle className="w-8 h-8 text-amber-500 mb-2" />
        <span className="text-xs text-slate-600 font-medium">{error}</span>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center bg-white border border-slate-200 rounded-xl p-6 text-center" style={{ height }}>
        <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider">No Data Available</span>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3" data-testid="bar-chart-card">
      {title && <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">{title}</h4>}
      <div style={{ width: '100%', height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            layout={layout}
            data={data}
            margin={{ top: 10, right: 20, left: layout === 'vertical' ? 40 : 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
            {layout === 'horizontal' ? (
              <>
                <XAxis dataKey={xAxisKey} tick={{ fill: '#64748B', fontSize: 11 }} axisLine={{ stroke: '#E2E8F0' }} />
                <YAxis tickFormatter={formatter} tick={{ fill: '#64748B', fontSize: 11 }} axisLine={{ stroke: '#E2E8F0' }} />
              </>
            ) : (
              <>
                <XAxis type="number" tickFormatter={formatter} tick={{ fill: '#64748B', fontSize: 11 }} axisLine={{ stroke: '#E2E8F0' }} />
                <YAxis dataKey={xAxisKey} type="category" tick={{ fill: '#64748B', fontSize: 11 }} axisLine={{ stroke: '#E2E8F0' }} />
              </>
            )}
            <Tooltip
              contentStyle={{ backgroundColor: '#0F172A', borderColor: '#334155', borderRadius: '8px', color: '#FFF', fontSize: '12px' }}
              formatter={(value, name) => [formatter(value), name]}
            />
            <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
            {bars.map((b) => (
              <Bar
                key={b.key}
                dataKey={b.key}
                name={b.name || b.key}
                fill={b.color || '#0052FF'}
                stackId={stacked ? 'a' : undefined}
                radius={layout === 'horizontal' ? [4, 4, 0, 0] : [0, 4, 4, 0]}
                onClick={(entry) => onBarClick && onBarClick(entry)}
                className={onBarClick ? 'cursor-pointer hover:opacity-80' : ''}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default BarChartComponent;
